import { useState } from 'react'

type UseHouseConsignmentsTableActionsProps = {
  rowIds: number[]
  onDeleteSelected: (ids: number[]) => Promise<void>
  isInlineEditActive: boolean
  hasUnsavedChanges: boolean
  isBulkDeleting: boolean
}

function useDeclarationItemsTableActions({
  rowIds,
  onDeleteSelected,
  isInlineEditActive,
  hasUnsavedChanges,
  isBulkDeleting,
}: UseHouseConsignmentsTableActionsProps) {
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(() => new Set())

  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedRowIds.has(id))
  const someSelected = rowIds.some((id) => selectedRowIds.has(id)) && !allSelected
  const selectedCount = selectedRowIds.size

  const hasActiveDirtyForm = isInlineEditActive && hasUnsavedChanges
  const isBulkDeleteNotAllowed = selectedCount === 0 || hasActiveDirtyForm || isBulkDeleting

  const toggleAllRowsSelected = async () => {
    setSelectedRowIds((prev) => {
      const shouldSelectAll = !rowIds.every((id) => prev.has(id))
      if (shouldSelectAll) {
        return new Set(rowIds)
      }
      return new Set()
    })
  }

  const toggleRowSelect = async (id: number) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const clearSelection = () => {
    setSelectedRowIds(new Set())
  }

  const deleteSelected = async () => {
    if (isBulkDeleteNotAllowed) return
    await onDeleteSelected(Array.from(selectedRowIds))
    clearSelection()
  }

  return {
    selectedRowIds,
    selectedCount,
    allSelected,
    someSelected,
    toggleAllRowsSelected,
    toggleRowSelect,
    clearSelection,
    isBulkDeleteNotAllowed,
    deleteSelected,
  }
}

export default useDeclarationItemsTableActions
